/**
 * Bundle PDP — turns a Super Subscriptions & Bundles offer into a real product page.
 *
 * Background: a bundle's anchor product (e.g. "Postpartum Bundle") is a $0
 * placeholder with no images and no description. Everything a shopper needs to see
 * lives in the app's own widget, which Shopify renders as an app block inside the
 * PDP. Theme Liquid runs before app blocks render, so none of it is reachable
 * server-side — this script is the bridge.
 *
 * Two shapes of offer, and the difference decides almost everything below:
 *
 *   product_group — a fixed bundle. Its contents and its price are settled before
 *     the shopper arrives, so the page can state both, and the app's widget is left
 *     with nothing to do; CSS takes it off the page (see _bundle-summary.liquid).
 *
 *   mix_and_match — a build-your-own box: selection groups with their own min/max,
 *     filled from a pool. There is no "the contents" until the shopper picks, and no
 *     price either. The widget is not redundant here, it is the configurator — so it
 *     stays, and this file frames it instead of replacing it: a gallery and a summary
 *     that follow the picks (see `watchPicks`), a "from" price for an empty box, and
 *     a full-width row explaining everything on offer.
 *
 * What it reads (all of it already in the initial HTML, no request needed):
 *   .sss-bundle-block[data-bundle-block-type=…][data-bundle-block-source="section"]
 *     data-bundle-block-id                  the offer's bundle-block id
 *     .sss-bundle-block__tier
 *       data-tier-title                     "Postpartum Pain Relief Bundle"
 *       data-price-type / data-price-value  percentage_off / 20
 *       product_group:
 *         data-group-products               [{productId, title, imageUrl}]
 *         .sss-bundle-block__group-row      one per member, data-quantity
 *       mix_and_match:
 *         [data-mix-group]                  data-mix-min / data-mix-max, one per group
 *           .sss-bundle-block__mix-card     one per option, data-group-product-id
 *
 * What it fetches: one Storefront API call for the member products' handle,
 * description, gallery images, vendor, price and availability — that last one decides
 * what stays on the page at all (see `pruneUnavailable`). That endpoint is open to
 * same-origin storefront requests without a token — it is how the app itself reads
 * the same products (see the vendor's fetcher-subscriptions.js). It 401s behind the
 * `shopify theme dev` localhost proxy, which is why every render path below has to
 * survive the fetch failing: titles, images and quantities come from the DOM, and
 * the app fills in real money strings on its own rows regardless.
 *
 * What it deliberately does NOT do: add to cart. The app already intercepts the
 * theme's Add to cart and Buy it now in the capture phase and posts the correctly
 * tagged multi-line payload itself (verified live: 5 lines, $100.93 -> $80.74 via a
 * real automatic discount). Re-implementing that here would mean duplicating a
 * contract — `_ssBundleGroup` / `_ssBundleBar` / `_ssBundleKey`, plus `_ssMixGroup`
 * on a build-your-own box — that the app's discount Function validates, with
 * nothing to gain. The one exception is the quantity shim at the foot of this file,
 * which rewrites the app's own payload rather than building its own.
 */
(() => {
  const STOREFRONT_API_VERSION = '2025-07';

  /**
   * The anchor product's title, handed in by sections/product-information.liquid.
   *
   * Read from the script element itself rather than from the page, so it is available
   * to the cart shim at the foot of this file even on a load where nothing else here
   * managed to hydrate. `document.currentScript` is valid throughout this IIFE's
   * synchronous run, `defer` included.
   */
  const ANCHOR_TITLE = (document.currentScript?.dataset.productTitle || '').trim();

  /** How long to wait for the app to fill its own money strings before giving up. */
  const APP_PRICE_TIMEOUT = 8000;

  const PRODUCT_QUERY = `
    query BundleMembers($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          id
          handle
          title
          vendor
          descriptionHtml
          description: metafield(namespace: "custom", key: "description") { value }
          featuredImage { url altText }
          images(first: 6) { nodes { url altText } }
          # The product's own options, so the one a variant carries beyond them — a
          # combined listing's combining option — can be told apart (see familyValue).
          options { name }
          # More than the first variant: a mix-and-match card can be picked on any of
          # its variants, and the cheapest one is what an unfilled box is priced "from".
          # Title and image so the page can show the one the shopper chose.
          variants(first: 20) {
            nodes {
              id
              title
              availableForSale
              price { amount }
              compareAtPrice { amount }
              selectedOptions { name value }
              image { url altText }
            }
          }
        }
      }
    }
  `;

  /* ---------------------------------------------------------------- helpers */

  /**
   * Money formatter mirroring Shopify's `money_format` placeholders. Only used
   * when we have to compute a total ourselves; anything the app has already
   * rendered is preferred verbatim, because that string is what the shop's own
   * Liquid produced and is guaranteed to match the checkout.
   */
  function formatMoney(cents, format) {
    const value = Math.round(cents) / 100;

    const withDelimiters = (precision, thousands, decimal) => {
      const fixed = Math.abs(value).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };

    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = (format || '${{amount}}').match(placeholder);
    const name = match ? match[1] : 'amount';

    let amount;
    switch (name) {
      case 'amount_no_decimals':
        amount = withDelimiters(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        amount = withDelimiters(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = withDelimiters(0, '.', ',');
        break;
      case 'amount_with_space_separator':
        amount = withDelimiters(2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = withDelimiters(0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = withDelimiters(2, "'", '.');
        break;
      default:
        amount = withDelimiters(2, ',', '.');
    }

    return (format || '${{amount}}').replace(placeholder, amount);
  }

  /** Cents from a Storefront API decimal string ("24.99" -> 2499). */
  function toCents(amount) {
    const value = Number.parseFloat(amount);
    return Number.isFinite(value) ? Math.round(value * 100) : 0;
  }

  /** Numeric id out of a `gid://shopify/Product/123` (or a bare id). */
  function numericId(gid) {
    const match = String(gid || '').match(/(\d+)\s*$/);
    return match ? match[1] : '';
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  /**
   * Description text, trimmed to a readable length on a word boundary.
   *
   * Plain text rather than the source HTML: these descriptions are authored for a
   * full PDP and carry their own headings and lists, which would fight the card
   * layout here — and injecting a third party's HTML into markup we build is not
   * something to do casually. `textContent` on a detached element strips tags
   * without ever parsing scripts into the live document.
   */
  function excerpt(html, limit) {
    const holder = document.createElement('div');
    holder.innerHTML = html || '';
    const text = (holder.textContent || '').replace(/\s+/g, ' ').trim();
    if (text.length <= limit) return text;
    const cut = text.slice(0, limit);
    const lastSpace = cut.lastIndexOf(' ');
    return `${(lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[,.;:\s]+$/, '')}…`;
  }

  /* --------------------------------------------------- telling siblings apart */

  /*
   * Several of this store's bundle options are members of a combined listing, and a
   * combined listing's members all carry the parent's title: four of the shampoos in
   * this box are called "Tear-Free Shampoo, Body Wash, & Bubbles" and nothing else.
   * The picker shows them as four identical cards, and the box a shopper builds reads
   * back as the same product three times over.
   *
   * The value that tells them apart — Strawberry, Blueberry — belongs to the *parent's*
   * combining option. The child's own `options` never list it, but on most families the
   * Storefront API does report it in each variant's `selectedOptions`, next to the
   * child's own ("Color: Honey Beee" beside "Size: 6-9 months"). That is the first
   * thing read below (`familyValue`).
   *
   * Some families carry nothing there: the shampoos report "Title / Default Title"
   * and no more, and this store's unauthenticated Storefront token has no metafield
   * scope. Liquid resolves a family from a child, but Liquid cannot see these products
   * at all — the offer is app-block markup that renders after it. What every child
   * does carry is a handle Shopify built from its full name at creation, which is the
   * title's slug plus exactly the part the title dropped:
   *
   *   title  "Tear-Free Shampoo, Body Wash, & Bubbles"
   *   handle  tear-free-shampoo-body-wash-bubbles-strawberry
   *                                              ^^^^^^^^^^
   *
   * So that is the fallback (`handleSuffix`). It only works while the title is still
   * the one the handle was made from: the Saga booties were retitled afterwards, so
   * their handles no longer start with their title's slug. The guards in
   * `labelFamilyMembers` keep both to the case they are good for. Nothing is written
   * to any product.
   */

  /**
   * The value a combined listing's child holds on its parent's combining option —
   * "Honey Beee", "Ocean Animals" — or '' when the Storefront API does not report one.
   *
   * It is whatever `selectedOptions` names that the product's own options do not, and
   * it has to be the same on every variant: a child is one member of its family.
   */
  function familyValue(node) {
    const own = new Set((node?.options || []).map((option) => option.name));
    const values = new Set();
    (node?.variants?.nodes || []).forEach((variant) => {
      (variant.selectedOptions || []).forEach((selected) => {
        if (!own.has(selected.name)) values.add(selected.value);
      });
    });
    return values.size === 1 ? [...values][0] : '';
  }

  /** A title slugged the way Shopify's `handleize` builds a default handle. */
  function titleSlug(title) {
    return (title || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      // Dropped rather than separated, as `handleize` does: "Baby's" -> "babys".
      .replace(/['‘’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /** The part of a product's handle its title does not account for, title-cased. */
  function handleSuffix(node) {
    const handle = node?.handle || '';
    const slug = titleSlug(node?.title);
    if (!slug || !handle.startsWith(slug)) return '';

    const rest = handle.slice(slug.length).replace(/^-+/, '');
    // Shopify's own disambiguators name nothing — a second product of the same title
    // gets `-1`, a duplicated one gets `-copy`.
    if (!rest || /^\d+$/.test(rest) || /^copy(-\d+)?$/.test(rest)) return '';

    return rest.replace(/-/g, ' ').replace(/(^|\s)[a-z]/g, (c) => c.toUpperCase());
  }

  /**
   * Give every member the name of the family member it is, by setting `variant` on it:
   * "Honey Beee" on booties whose title never says which pair they are.
   *
   * Where the Storefront API reports that name (`familyValue`) it is used on any
   * member, sibling or not. It is data rather than a guess, and a lone child needs it
   * as much as a pair does: its title is its family's, so without it a box of Saga
   * booties never says Sunbaked Bloom anywhere.
   *
   * The handle fallback is kept to members that share their title with a sibling on
   * the same shelf. On a product with a title of its own there is nothing to tell
   * apart, and a handle suffix there is as likely to be a slug collision as a name.
   * And it is all or nothing within a set of siblings: labelling three of four
   * shampoos leaves the fourth looking like the plain, default one, which is a worse
   * answer than four identical cards. So a set is only named when every member yields
   * a name and all of them differ.
   */
  function labelFamilyMembers(members, data) {
    const byTitle = new Map();
    members.forEach((member) => {
      const title = data.get(member.id)?.title || member.title;
      if (!byTitle.has(title)) byTitle.set(title, []);
      byTitle.get(title).push(member);
    });

    byTitle.forEach((sharing) => {
      if (sharing.length < 2) return;
      const labels = sharing.map((member) => {
        const node = data.get(member.id);
        return familyValue(node) || handleSuffix(node);
      });
      if (labels.some((label) => !label)) return;
      if (new Set(labels).size !== labels.length) return;
      sharing.forEach((member, index) => {
        member.variant = labels[index];
      });
    });

    members.forEach((member) => {
      if (!member.variant) member.variant = familyValue(data.get(member.id));
    });
  }

  /**
   * Name the variant an option will be added on when the shopper has no say in it,
   * by setting `only` on it: "6-9 months" on a bootie that comes in one size.
   *
   * The app only draws a variant picker on a card with more than one variant to
   * choose from, and says nothing on one without — so a one-size product is added in
   * a size the shopper was never shown. Its own options only (`familyValue` names the
   * rest), and never Shopify's "Default Title" placeholder. (A fixed bundle's row
   * needs none of this: there the app names the variant itself where it draws no
   * picker.)
   */
  function nameOnlyVariants(options, data) {
    options.forEach((option) => {
      const node = data.get(option.id);
      const variants = allowedVariants(node, option.card);
      if (variants.length !== 1) return;

      const own = new Set((node.options || []).map((entry) => entry.name));
      const name = (variants[0].selectedOptions || [])
        .filter((selected) => own.has(selected.name) && selected.value !== 'Default Title')
        .map((selected) => selected.value)
        .join(' / ');
      if (name) option.only = name;
    });
  }

  /**
   * The variant a member will be added on, as the app has it right now, or null.
   *
   * A fixed bundle's row carries it as `data-selected-variant-id`, which the app
   * seeds before its own Storefront call and rewrites on every change of its selects.
   * A build-your-own card has no such attribute: the choice lives in the select the
   * app draws once the card is picked, and an unpicked card has made none.
   */
  function chosenVariant(member, data) {
    const node = data.get(member.id);
    if (!node) return null;
    const id =
      member.row?.dataset.selectedVariantId ||
      member.card?.querySelector('select.sss-bundle-block__mix-card-variant')?.value ||
      '';
    if (!id) return null;
    return (node.variants?.nodes || []).find((variant) => numericId(variant.id) === numericId(id)) || null;
  }

  /**
   * The chosen variant's own name ("Storm / 2 - 3 T"), where the shopper had one to
   * make. A member with a single allowed variant made no choice, and says what it is
   * through `only` instead.
   */
  function chosenName(member, data) {
    if (allowedVariants(data.get(member.id), member.card || member.row).length < 2) return '';
    const title = chosenVariant(member, data)?.title || '';
    return title === 'Default Title' ? '' : title;
  }

  /** The picture of what the shopper is getting: the chosen variant's, else the product's. */
  function memberImage(member, data) {
    return chosenVariant(member, data)?.image?.url || data.get(member.id)?.featuredImage?.url || member.image;
  }

  /** The product page for a member, opened on the variant the box holds where there is one. */
  function memberUrl(member, data) {
    const node = data.get(member.id);
    if (!node?.handle) return '';
    const variant = chosenName(member, data) ? chosenVariant(member, data) : null;
    return variant ? `/products/${node.handle}?variant=${numericId(variant.id)}` : `/products/${node.handle}`;
  }

  /**
   * The name to show for a member: its title, then which family member it is and
   * which variant the box holds.
   */
  function memberTitle(member, data) {
    const title = data.get(member.id)?.title || member.title;
    const detail = [member.variant, chosenName(member, data) || member.only].filter(Boolean).join(' · ');
    return detail ? `${title} – ${detail}` : title;
  }

  /** One line of ours under the app's own title node, created once and kept current. */
  function titleLine(host, titleSelector, attribute, className, text) {
    const existing = host.querySelector(`[${attribute}]`);
    if (!text) {
      existing?.remove();
      return;
    }

    let line = existing;
    if (!line) {
      const title = host.querySelector(titleSelector);
      if (!title) return;
      line = el('span', className);
      line.setAttribute(attribute, '');
      // After the family name when there is one, so the two read top to bottom.
      (host.querySelector('[data-bundle-variant-label]') || title).insertAdjacentElement('afterend', line);
    }
    if (line.textContent !== text) line.textContent = text;
  }

  /**
   * Write those names onto the app's own widget: its option cards on a build-your-own
   * box, its rows on a fixed bundle.
   *
   * Added as elements of ours rather than folded into the app's title node: that
   * node's text is the app's, rewritten whenever it redraws a card or re-prices a
   * row, and a label put inside it would not survive the shopper's first pick.
   * Idempotent, because the observers that keep the page in step re-run this on
   * every mutation.
   */
  function labelWidget(offer) {
    if (offer.kind === 'mix_and_match') {
      offer.groups.forEach((group) => {
        group.options.forEach((option) => {
          titleLine(option.card, '[data-mix-card-title]', 'data-bundle-variant-label', 'bundle-picker__variant', option.variant);
          titleLine(option.card, '[data-mix-card-title]', 'data-bundle-variant-only', 'bundle-picker__variant-only', option.only);
        });
      });
      return;
    }

    offer.members.forEach((member) => {
      titleLine(member.row, '[data-group-title]', 'data-bundle-variant-label', 'bundle-picker__variant', member.variant);
    });
  }

  /** Shopify CDN images take a width param; keeps thumbnails from shipping 2000px. */
  function sized(url, width) {
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch {
      return url;
    }
  }

  /* ------------------------------------------------------- reading the offer */

  /**
   * The app block this page's offer lives in, or null.
   *
   * A merchant running both the App Embed and the Product block gets every bar
   * rendered twice — once inside the section, once at the end of `<body>`. The app
   * collapses that at init by removing the *embed* copy
   * (`dropCrossSourceDuplicates`), so the section copy is the one that survives to
   * be wired up, priced and read by the shopper. Ask for it by name rather than
   * relying on document order, or a page whose embed happens to come first hands us
   * a node the app is about to detach.
   */
  function findOfferBlock(type) {
    const blocks = [...document.querySelectorAll(`.sss-bundle-block[data-bundle-block-type="${type}"]`)];
    return blocks.find((block) => block.dataset.bundleBlockSource === 'section') || blocks[0] || null;
  }

  /** The tier (bar) whose contents this page describes. */
  function readTier(block) {
    // Tier selection is the app's own state. Falling back to the first tier keeps
    // the page from rendering blank in the window before the app has hydrated.
    return block.querySelector('.sss-bundle-block__tier--selected') || block.querySelector('.sss-bundle-block__tier');
  }

  /** The bar-level fields both offer shapes share. */
  function readDeal(block, tier) {
    return {
      block,
      tier,
      name: (tier.dataset.tierTitle || '').trim(),
      priceType: tier.dataset.priceType || 'full',
      priceValue: Number.parseFloat(tier.dataset.priceValue || '0') || 0,
      moneyFormat: block.dataset.moneyFormat || '${{amount}}',
    };
  }

  /**
   * A fixed bundle: every member is in the box, at a quantity the merchant set.
   * Quantity/blank-variant guards matter: a member whose row the app could not
   * resolve is a bundle we should not be describing.
   */
  function readProductGroupOffer() {
    const block = findOfferBlock('product_group');
    if (!block) return null;
    const tier = readTier(block);
    if (!tier) return null;

    let groupProducts;
    try {
      groupProducts = JSON.parse(tier.dataset.groupProducts || '[]');
    } catch {
      groupProducts = [];
    }

    const byId = new Map();
    groupProducts.forEach((entry) => {
      const id = numericId(entry.productId);
      if (id) byId.set(id, entry);
    });

    const members = [...tier.querySelectorAll('.sss-bundle-block__group-row')]
      .map((row) => {
        const id = row.dataset.groupProductId || '';
        const source = byId.get(id) || {};
        return {
          id,
          row,
          quantity: Math.max(1, parseInt(row.dataset.quantity || '1', 10)),
          title: (source.title || row.querySelector('[data-group-title]')?.textContent || '').trim(),
          image: source.imageUrl || row.querySelector('[data-group-image]')?.getAttribute('src') || '',
        };
      })
      .filter((member) => member.id);

    if (members.length === 0) return null;

    return { kind: 'product_group', ...readDeal(block, tier), members };
  }

  /**
   * A build-your-own box: one or more selection groups, each with its own
   * min/max, filled from a pool of candidate products.
   *
   * The shopper's picks are the app's own state (a WeakMap keyed by card element),
   * so they are not readable from here — but every pick is mirrored onto the DOM:
   * `.is-selected` on the card and a count in its stepper. `readPicks` below reads
   * them back out of that, which is why nothing here caches a selection.
   *
   * `members` is the deduplicated pool. The same product may be offered in more
   * than one group, and it is one Storefront record either way.
   */
  function readMixOffer() {
    const block = findOfferBlock('mix_and_match');
    if (!block) return null;
    const tier = readTier(block);
    if (!tier) return null;

    const seen = new Map();

    const groups = [...tier.querySelectorAll('[data-mix-group]')].map((el, index) => {
      const options = [...el.querySelectorAll('.sss-bundle-block__mix-card')]
        .map((card) => {
          const id = card.dataset.groupProductId || '';
          if (!id) return null;
          const option = {
            id,
            card,
            title: (card.querySelector('[data-mix-card-title]')?.textContent || '').trim(),
            image: card.querySelector('[data-mix-card-image]')?.getAttribute('src') || '',
          };
          if (!seen.has(id)) seen.set(id, option);
          return option;
        })
        .filter(Boolean);

      return {
        el,
        index,
        options,
        label: (el.querySelector('.sss-bundle-block__mix-group-label')?.textContent || '').trim(),
        min: Math.max(0, parseInt(el.dataset.mixMin || '0', 10) || 0),
        max: Math.max(0, parseInt(el.dataset.mixMax || '0', 10) || 0),
      };
    });

    const filled = groups.filter((group) => group.options.length > 0);
    if (filled.length === 0) return null;

    // Stamped here rather than at first paint because the app fills its own counters
    // on its init, which may land before this script's — and a group whose ceiling
    // is not a real one has to stay blank until `relabelPickCounters` has restated
    // it, or the ceiling flashes up before it is taken back down.
    filled.forEach((group) => {
      if (pickMax(group) === null) group.el.dataset.pickUnbounded = 'true';
    });

    const bounded = filled.every((group) => pickMax(group) !== null);

    return {
      kind: 'mix_and_match',
      ...readDeal(block, tier),
      groups: filled,
      members: [...seen.values()],
      minPicks: filled.reduce((sum, group) => sum + group.min, 0),
      // null, not a number, when any group is open-ended: the app's stand-in ceiling
      // summed into a total ("up to 500 products") is the same fiction the picker no
      // longer prints, and the assistant would repeat it as fact.
      maxPicks: bounded ? filled.reduce((sum, group) => sum + pickMax(group), 0) : null,
      // `includeCurrent` pins the anchor product into every box. Bundle anchors on
      // this theme are $0 placeholders, so no offer we serve sets it — but read it
      // rather than assume, so a box that does include it is not described short.
      includesCurrent: tier.querySelector('[data-mix-container]')?.dataset.mixIncludeCurrent === 'true',
    };
  }

  /**
   * Where a group's maximum stops being a limit and starts being a way of writing
   * "no limit". Bundles that really do cap a shelf cap it at a handful — "pick 2 of
   * these 6" — so the line sits well clear of any number chosen on purpose.
   */
  const UNBOUNDED_PICK_MAX = 20;

  /**
   * A selection group's upper limit as the page should state it, or null where it
   * has none worth stating.
   *
   * The app requires a number in a group's max field, so an offer that means "take
   * as many as you like" says it by setting one nobody will reach — this store's
   * bath box carries 100 against shelves of two to four. The app enforces it either
   * way; what it cannot do is tell a cap apart from a stand-in, so its own counter
   * reads "0 / 100 · Pick 1 to 100" and turns the floor the box actually asks for
   * into an invitation to buy ninety-nine bath toys.
   *
   * (The count that ceiling governs is UNITS, not products: the app's steppers let
   * any option repeat regardless of the group's `allow-multiple` flag, which its
   * storefront script never reads. So a shelf of three options with a max of 100 is
   * not out of reach — it is just not a number anybody meant.)
   *
   * `max === min` is not a range at all but a fixed count ("Pick 3"), and comes back
   * as the number so callers can say so.
   */
  function pickMax(group) {
    const max = group.max;
    if (max <= 0 || max < group.min) return null;
    if (max === group.min) return max;
    return max < UNBOUNDED_PICK_MAX ? max : null;
  }

  /** How a group's requirement reads on a label: "Pick 3", "Pick 1–2", "Pick 1+". */
  function pickLabel(group) {
    const max = pickMax(group);
    if (max !== null) return max > group.min ? `Pick ${group.min}–${max}` : `Pick ${max}`;
    return group.min > 0 ? `Pick ${group.min}+` : 'Optional';
  }

  /** Whichever kind of bundle this page is, or null if it is neither. */
  function readOffer() {
    return readProductGroupOffer() || readMixOffer();
  }

  /**
   * What the shopper has chosen right now, read back off the app's own cards.
   *
   * One entry per picked *product* per group (not per unit) carrying `quantity`,
   * because that is the shape both the summary receipt and the gallery want. The
   * stepper only exists on a selected card, so a selected card with no stepper yet
   * — the frame between the class landing and the re-render — counts as one.
   */
  function readPicks(offer) {
    return offer.groups.map((group) => {
      const picks = group.options
        .map((option) => {
          if (!option.card.classList.contains('is-selected')) return null;
          const count = Number.parseInt(
            option.card.querySelector('.sss-bundle-block__mix-step-count')?.textContent || '1',
            10
          );
          return { ...option, quantity: Number.isFinite(count) && count > 0 ? count : 1 };
        })
        .filter(Boolean);

      return { group, picks, count: picks.reduce((sum, pick) => sum + pick.quantity, 0) };
    });
  }

  /**
   * A stable string that changes exactly when the shopper's picks change — the
   * variant a pick is on included, so a new size or colour re-renders its photo.
   */
  function picksSignature(chosen) {
    const variantOf = (pick) => pick.card?.querySelector('select.sss-bundle-block__mix-card-variant')?.value || '';
    return chosen.map(({ picks }) => picks.map((p) => `${p.id}x${p.quantity}@${variantOf(p)}`).join(',')).join('|');
  }

  /** One Storefront call for every member. Resolves to a Map, empty if it fails. */
  async function fetchMembers(offer) {
    const ids = offer.members.map((member) => `gid://shopify/Product/${member.id}`);
    try {
      const response = await fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ query: PRODUCT_QUERY, variables: { ids } }),
      });
      if (!response.ok) throw new Error(`Storefront API ${response.status}`);

      const payload = await response.json();
      const nodes = payload?.data?.nodes || [];
      const map = new Map();
      nodes.forEach((node) => {
        if (node?.id) map.set(numericId(node.id), node);
      });
      return map;
    } catch (error) {
      // Expected behind `shopify theme dev` (the proxy origin is not the shop's),
      // so this is a downgrade rather than a failure: the page still renders from
      // the DOM, minus descriptions and product links.
      console.warn('[bundle-pdp] member lookup unavailable, rendering from the widget alone:', error.message);
      return new Map();
    }
  }

  /* ------------------------------------------------------------ price wiring */

  /** The app's own money strings, once it has finished pricing the widget. */
  function appTotals(offer) {
    const total = offer.tier.querySelector('[data-tier-total]')?.textContent.trim() || '';
    // The app hides the strike-through by style rather than by emptying it — on a
    // box whose discount nets to nothing, and on every mix bar before the first
    // pick. Reading its text regardless would resurrect a stale compare-at price
    // next to a total it no longer relates to.
    const compareNode = offer.tier.querySelector('[data-tier-compare]');
    const compare = compareNode && compareNode.style.display !== 'none' ? compareNode.textContent.trim() : '';
    return total ? { total, compare } : null;
  }

  /**
   * The bar's price treatment applied to a box that would otherwise cost
   * `originalCents`, or null for a treatment we do not know.
   *
   * Mirrors the vendor's own arithmetic (fetcher-subscriptions.js): `percentage_off`
   * scales the pack, `amount_off` subtracts a flat amount, and `fixed` is the whole
   * pack's price rather than a per-unit one — and never raises it above full price.
   * Only ever used as a fallback, so a future price type we do not know about
   * simply leaves the app's own string in place instead of inventing a number.
   */
  function applyDeal(offer, originalCents) {
    if (offer.priceType === 'percentage_off' && offer.priceValue > 0) {
      return Math.round(originalCents * (1 - offer.priceValue / 100));
    }
    if (offer.priceType === 'amount_off' && offer.priceValue > 0) {
      return Math.round(Math.max(0, originalCents - offer.priceValue * 100));
    }
    if (offer.priceType === 'fixed' && offer.priceValue > 0) {
      return Math.round(Math.min(originalCents, offer.priceValue * 100));
    }
    return null;
  }

  /** The money strings for a box we priced ourselves. */
  function totalsFrom(offer, originalCents) {
    const finalCents = applyDeal(offer, originalCents);
    if (finalCents == null) return null;
    return {
      originalCents,
      finalCents,
      total: formatMoney(finalCents, offer.moneyFormat),
      compare: formatMoney(originalCents, offer.moneyFormat),
      save: formatMoney(originalCents - finalCents, offer.moneyFormat),
    };
  }

  /**
   * The variants a member's price can honestly come from: the ones that can be
   * bought, or all of them where none can.
   *
   * The app adds a card on its first AVAILABLE variant, so pricing from a sold-out
   * cheaper one would quote a number the box can never cost — a 4oz balm's price
   * against a box that will hold the 8oz. The fallback keeps a member that is
   * entirely sold out priced rather than blank; by the time this runs those are off
   * the page anyway, and the only offer that still holds one is already unavailable.
   */
  function memberVariants(data, id) {
    const variants = data.get(id)?.variants?.nodes || [];
    const sellable = variants.filter((variant) => variant.availableForSale);
    return sellable.length ? sellable : variants;
  }

  function memberUnitCents(data, id) {
    const variant = memberVariants(data, id)[0];
    return variant ? toCents(variant.price?.amount) : null;
  }

  function memberFloorCents(data, id) {
    const prices = memberVariants(data, id)
      .map((variant) => toCents(variant.price?.amount))
      .filter((cents) => cents > 0);
    return prices.length ? Math.min(...prices) : null;
  }

  /** What a fixed bundle costs, from the member prices we fetched. */
  function computeTotals(offer, data) {
    let originalCents = 0;
    let known = 0;

    offer.members.forEach((member) => {
      const cents = memberUnitCents(data, member.id);
      if (cents == null) return;
      known += 1;
      originalCents += cents * member.quantity;
    });

    if (known !== offer.members.length || originalCents <= 0) return null;

    return totalsFrom(offer, originalCents);
  }

  /**
   * What the box the shopper has actually built costs.
   *
   * Priced on each pick's *first* variant, which is what the card shows until they
   * change it. That is a fallback either way: once the app has priced the bar we
   * take its strings instead (they know the chosen variant), and this only has to
   * hold the page together in the window before that lands.
   */
  function computeMixTotals(offer, data, chosen) {
    let originalCents = 0;
    let priced = 0;
    let units = 0;

    chosen.forEach(({ picks }) => {
      picks.forEach((pick) => {
        units += pick.quantity;
        const cents = memberUnitCents(data, pick.id);
        if (cents == null) return;
        priced += pick.quantity;
        originalCents += cents * pick.quantity;
      });
    });

    if (units === 0 || priced !== units || originalCents <= 0) return null;

    return totalsFrom(offer, originalCents);
  }

  /**
   * The cheapest box that would actually satisfy the offer — every group filled to
   * its minimum with its cheapest option, on that option's cheapest variant.
   *
   * This is the page's price before the shopper has chosen anything. A bundle whose
   * anchor is a $0 placeholder has no honest price of its own, and "build your box"
   * with no number at all reads as unpriced rather than as flexible; "From $X" is
   * both true and the thing a shopper wants to know first.
   */
  function computeMixFloor(offer, data) {
    let originalCents = 0;

    for (const group of offer.groups) {
      if (group.min <= 0) continue;
      const cheapest = group.options
        .map((option) => memberFloorCents(data, option.id))
        .filter((cents) => cents != null && cents > 0);
      // A group with nothing priceable in it cannot be costed, and a floor missing
      // one of its groups is not a floor. (Options nobody can buy — sold out, or
      // unknown to the storefront — are already gone by here; see `pruneUnavailable`.)
      if (cheapest.length === 0) return null;
      originalCents += Math.min(...cheapest) * group.min;
    }

    if (originalCents <= 0) return null;
    return totalsFrom(offer, originalCents);
  }

  /**
   * The variants an option can actually be added on.
   *
   * The app's own subset where the offer names one (`data-variant-ids`), all of the
   * product's otherwise — and its own fallback with it: a subset that resolves to
   * nothing is treated as no subset at all, so a stale id list left behind by a
   * re-made product does not read as a sold-out one.
   */
  function allowedVariants(node, el) {
    const variants = node?.variants?.nodes || [];
    const raw = (el?.dataset.variantIds || '').trim();
    if (!raw) return variants;
    const allow = new Set(raw.split(',').map((id) => id.trim()).filter(Boolean));
    const named = variants.filter((variant) => allow.has(numericId(variant.id)));
    return named.length ? named : variants;
  }

  /**
   * Whether a member can be bought right now.
   *
   * `availableForSale` rather than an inventory number: a product the merchant sells
   * past zero is available, and one whose last unit went a second ago is not — the
   * same answer its own PDP gives, arrived at the same way.
   *
   * A member the storefront did not return at all fails here too. A merchant can
   * leave a product in an offer after unpublishing it, and two of this store's do
   * exactly that; the app hits the same wall (its hydration skips a card with no
   * product behind it, so the card is never wired, never priced, and cannot be
   * tapped). Either way it is not something a shopper can put in their box.
   */
  function isSellable(member, data) {
    const node = data.get(member.id);
    if (!node) return false;
    return allowedVariants(node, member.card || member.row).some((variant) => variant.availableForSale);
  }

  /**
   * The offer with everything that cannot be bought taken out of it — or, where that
   * leaves a box nobody could complete, an `unavailable` list naming what is missing.
   *
   * A bundle is short in one of two ways, and they want opposite treatment.
   *
   * An option inside a build-your-own group is one of several answers to the same
   * question, so a sold-out one is dropped and the shopper chooses from the rest
   * without ever knowing it was there. Leaving it up is the worse state: the card
   * sits with a title, a photo and an empty space where every other card has a
   * price, which reads as a product whose price "isn't showing" rather than as one
   * that is not for sale — and tapping it either does nothing or, on a card the app
   * did wire, puts a variant in the box that checkout will reject.
   *
   * A group with no answers left, or any member of a fixed bundle, is not optional:
   * the box cannot be assembled at all. That is not something to prune, it is the
   * end of the offer — `renderUnavailable` takes the whole thing off sale rather
   * than walking a shopper through filling a box the app will refuse at the last
   * step. Group minimums are never touched to paper over it: they are what the
   * offer requires, not a count of what is left to choose from.
   *
   * Nothing at all happens when the lookup came back empty. That is a failed request
   * (it 401s behind `shopify theme dev`), not a sold-out catalogue, and a bundle
   * taken off sale by a network blip is a worse outcome than a stale card.
   */
  function pruneUnavailable(offer, data) {
    if (data.size === 0) return offer;

    if (offer.kind !== 'mix_and_match') {
      // Every member of a fixed bundle is required, so one of them is all it takes.
      const missing = offer.members.filter((member) => !isSellable(member, data));
      return missing.length ? { ...offer, unavailable: missing.map((member) => memberTitle(member, data)) } : offer;
    }

    const groups = offer.groups.map((group) => ({
      ...group,
      options: group.options.filter((option) => isSellable(option, data)),
    }));

    // Named rather than counted: "Bath Toys" is what a merchant needs to hear to fix
    // this, and what the assistant needs to answer "why can't I buy this".
    const emptied = groups.filter((group) => group.min > 0 && group.options.length === 0);
    if (emptied.length) return { ...offer, unavailable: emptied.map((group) => group.label) };

    // What survives. A group that owed no pick and has nothing left to offer is not
    // a broken box, just an empty shelf, and comes off the page with its cards.
    const live = groups.filter((group) => group.options.length > 0);
    const liveGroups = new Set(live.map((group) => group.el));
    const liveCards = new Set(live.flatMap((group) => group.options.map((option) => option.card)));
    const liveIds = new Set(live.flatMap((group) => group.options.map((option) => option.id)));

    offer.groups.forEach((group) => {
      group.el.hidden = !liveGroups.has(group.el);
      group.options.forEach((option) => {
        option.card.hidden = !liveCards.has(option.card);
      });
    });

    return {
      ...offer,
      groups: live,
      // By id, not by card: the same product can be offered in two groups, and the
      // pool holds one entry (with one group's card) for both.
      members: offer.members.filter((member) => liveIds.has(member.id)),
    };
  }

  /**
   * Resolve once the app has written its tier total, or immediately if it already
   * has. The widget prices itself after its own Storefront round trip, so the
   * element exists long before it has text in it.
   */
  function whenAppPriced(offer) {
    if (appTotals(offer)) return Promise.resolve(true);

    return new Promise((resolve) => {
      const done = (value) => {
        observer.disconnect();
        clearTimeout(timer);
        resolve(value);
      };
      const observer = new MutationObserver(() => {
        if (appTotals(offer)) done(true);
      });
      const timer = setTimeout(() => done(false), APP_PRICE_TIMEOUT);
      observer.observe(offer.tier, { childList: true, subtree: true, characterData: true });
    });
  }

  /* ---------------------------------------------------------------- renderers */

  /** How many products the flat-lay can hold before it has to say "+N more". */
  const COLLAGE_TILES = 6;

  /**
   * The gallery of a fixed bundle: everything in the box, then each member's own
   * photography. Both follow the variant each row is set to — the jacket the shopper
   * switched to Storm is the Storm jacket in the flat-lay and first in its own slides.
   */
  function buildGallerySlides(offer, data) {
    // Slide 0 is a flat-lay of everything in the box. A bundle whose hero is one
    // member's packshot reads as that single product with a confusing price on it;
    // seeing all five at once is the whole proposition of the page.
    const slides = [
      {
        type: 'collage',
        label: offer.name || 'Everything included',
        tiles: offer.members.map((member) => ({ url: memberImage(member, data), alt: memberTitle(member, data) })).filter((t) => t.url),
      },
    ];

    // Then the members' own photography, so the gallery still answers "what does
    // this actually look like" the way any other PDP would.
    offer.members.forEach((member) => {
      const node = data.get(member.id);
      const name = memberTitle(member, data);
      let images = node?.images?.nodes?.length ? node.images.nodes : [{ url: member.image, altText: name }];
      const chosenUrl = chosenVariant(member, data)?.image?.url;
      if (chosenUrl) images = [{ url: chosenUrl, altText: name }, ...images.filter((image) => image?.url !== chosenUrl)];
      images.slice(0, node ? 3 : 1).forEach((image, index) => {
        if (!image?.url) return;
        slides.push({
          type: 'image',
          url: image.url,
          alt: image.altText || name,
          label: name,
          primary: index === 0,
        });
      });
    });

    return slides;
  }

  /**
   * The gallery of a build-your-own box, which has no fixed contents to photograph.
   *
   * Once anything is picked the hero *is* the shopper's box, and it keeps up as they
   * change it — that running flat-lay is the closest this page gets to showing the
   * product it is selling. Before then it shows one option from each group, so an
   * untouched page still says "bath toys, bubble bath, lotion" at a glance rather
   * than sitting empty.
   *
   * One image per product here, unlike a fixed bundle: the pool is far bigger than a
   * box, and a rail of thirty photographs is a worse answer to "what is in this" than
   * a rail of the ten things the shopper chose. The full pool, with descriptions, is
   * what the "what's inside" row below is for.
   */
  function buildMixGallerySlides(offer, data, chosen) {
    const imageOf = (member) => memberImage(member, data);

    const picked = chosen.flatMap(({ picks }) => picks);
    const shown = picked.length ? picked : offer.groups.map((group) => group.options[0]).filter(Boolean);

    const tiles = shown.map((member) => ({ url: imageOf(member), alt: memberTitle(member, data) })).filter((tile) => tile.url);

    // The flat-lay is a fixed six cells; a fuller box spends the last of them on a
    // "+N" rather than overflowing its frame or silently dropping what it can't fit.
    const overflowing = tiles.length > COLLAGE_TILES;

    const slides = [
      {
        type: 'collage',
        label: picked.length ? 'Your box' : offer.name || 'What you can choose from',
        tiles: tiles.slice(0, overflowing ? COLLAGE_TILES - 1 : COLLAGE_TILES),
        overflow: overflowing ? tiles.length - (COLLAGE_TILES - 1) : 0,
      },
    ];

    shown.forEach((member) => {
      const url = imageOf(member);
      if (!url) return;
      slides.push({
        type: 'image',
        url,
        alt: data.get(member.id)?.featuredImage?.altText || memberTitle(member, data),
        label: memberTitle(member, data),
        primary: true,
      });
    });

    return slides;
  }

  function renderGallery(root, offer, data, chosen) {
    const stage = root.querySelector('[data-bundle-gallery-stage]');
    const rail = root.querySelector('[data-bundle-gallery-rail]');
    const counter = root.querySelector('[data-bundle-gallery-counter]');
    if (!stage || !rail) return;

    const slides =
      offer.kind === 'mix_and_match'
        ? buildMixGallerySlides(offer, data, chosen)
        : buildGallerySlides(offer, data);

    if (slides[0].tiles.length === 0 && slides.length === 1) return;

    stage.textContent = '';
    rail.textContent = '';

    slides.forEach((slide, index) => {
      const figure = el('div', 'bundle-gallery__slide');
      figure.dataset.bundleSlide = String(index);
      if (index === 0) figure.dataset.current = 'true';

      if (slide.type === 'collage') {
        const collage = el('div', 'bundle-gallery__collage');
        const cells = slide.tiles.length + (slide.overflow ? 1 : 0);
        collage.dataset.count = String(Math.min(cells, COLLAGE_TILES));
        slide.tiles.forEach((tile) => {
          const cell = el('span', 'bundle-gallery__tile');
          const image = new Image();
          image.src = sized(tile.url, 500);
          image.alt = tile.alt;
          image.loading = 'eager';
          cell.appendChild(image);
          collage.appendChild(cell);
        });
        if (slide.overflow) {
          collage.appendChild(el('span', 'bundle-gallery__tile bundle-gallery__tile--more', `+${slide.overflow}`));
        }
        figure.appendChild(collage);
      } else {
        const image = new Image();
        image.src = sized(slide.url, 1200);
        image.alt = slide.alt;
        image.loading = 'lazy';
        image.className = 'bundle-gallery__image';
        figure.appendChild(image);
      }
      stage.appendChild(figure);

      // Only the collage and each member's lead image earn a thumbnail — the rail
      // is a map of what is in the box, not of every photo behind it.
      if (slide.type === 'image' && !slide.primary) return;

      const thumb = el('button', 'bundle-gallery__thumb');
      thumb.type = 'button';
      thumb.dataset.bundleThumb = String(index);
      thumb.setAttribute('aria-label', slide.label);
      if (index === 0) {
        thumb.dataset.current = 'true';
        thumb.classList.add('bundle-gallery__thumb--collage');
        slide.tiles.slice(0, 4).forEach((tile) => {
          const image = new Image();
          image.src = sized(tile.url, 120);
          image.alt = '';
          image.loading = 'lazy';
          thumb.appendChild(image);
        });
      } else {
        const thumbImage = new Image();
        thumbImage.src = sized(slide.url, 200);
        thumbImage.alt = '';
        thumbImage.loading = 'lazy';
        thumb.appendChild(thumbImage);
      }
      rail.appendChild(thumb);
    });

    /**
     * "3 / 8" over the foot of the stage — what stands in for the rail on a phone,
     * where the rail is off (see blocks/_bundle-gallery.liquid).
     *
     * Counts the slides in the DOM rather than closing over `slides`: the listeners
     * below are bound once for the life of the page, while a build-your-own box
     * rebuilds this gallery every time the shopper changes a pick, so the array this
     * render can see is not the one on screen by the time they swipe.
     */
    const setCounter = (index) => {
      if (!counter) return;
      const total = stage.querySelectorAll('[data-bundle-slide]').length;
      counter.textContent = total > 1 ? `${Number(index) + 1} / ${total}` : '';
      counter.hidden = total <= 1;
    };

    const select = (index) => {
      stage.querySelectorAll('[data-bundle-slide]').forEach((slide) => {
        slide.toggleAttribute('data-current', slide.dataset.bundleSlide === String(index));
      });
      rail.querySelectorAll('[data-bundle-thumb]').forEach((thumb) => {
        thumb.toggleAttribute('data-current', thumb.dataset.bundleThumb === String(index));
      });
      setCounter(index);
      const active = stage.querySelector(`[data-bundle-slide="${index}"]`);
      if (active) active.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'smooth' });
    };

    setCounter(0);

    // Bound once, not once per render: a build-your-own box redraws this gallery
    // every time the shopper changes a pick, and re-binding here would stack a
    // fresh pair of listeners on the same two elements on every tap.
    if (root.dataset.bundleGalleryWired === 'true') {
      root.dataset.bundleGalleryReady = 'true';
      return;
    }
    root.dataset.bundleGalleryWired = 'true';

    rail.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-bundle-thumb]');
      if (thumb) select(Number(thumb.dataset.bundleThumb));
    });

    // The stage scrolls natively on touch; keep the rail's highlight and the counter
    // honest.
    stage.addEventListener(
      'scroll',
      () => {
        const middle = stage.scrollLeft + stage.clientWidth / 2;
        let closest = null;
        stage.querySelectorAll('[data-bundle-slide]').forEach((slide) => {
          const center = slide.offsetLeft + slide.clientWidth / 2;
          if (!closest || Math.abs(center - middle) < Math.abs(closest.center - middle)) {
            closest = { index: slide.dataset.bundleSlide, center };
          }
        });
        if (!closest) return;
        rail.querySelectorAll('[data-bundle-thumb]').forEach((thumb) => {
          thumb.toggleAttribute('data-current', thumb.dataset.bundleThumb === closest.index);
        });
        setCounter(closest.index);
      },
      { passive: true }
    );

    root.dataset.bundleGalleryReady = 'true';
  }

  /**
   * The eyebrow's "how much is in here" half, or '' where there is nothing useful to
   * say.
   *
   * A fixed bundle gets a count, because "5 products" is a fact about the thing being
   * sold and there is nowhere else on the page to learn it.
   *
   * A build-your-own box gets nothing. "Pick 5–10 products" is an instruction, not a
   * description, and it is one the picker gives far better: every group states its own
   * "Pick 1+" against the cards it applies to, where the shopper is actually
   * choosing. Summing those into a single range at the top of the page adds a number
   * nobody can act on — and it is the first thing under the title, so it displaces the
   * price to say it.
   */
  function countLabel(offer) {
    if (offer.kind === 'mix_and_match') return '';
    const pieces = offer.members.reduce((sum, member) => sum + member.quantity, 0);
    return `${pieces} products`;
  }

  /**
   * The summary's item list.
   *
   * A fixed bundle lists what is in the box. A build-your-own box lists what the
   * shopper has put in it so far, and — for any group still short of its minimum —
   * a row saying so, which scrolls to that group in the picker. So the same list
   * doubles as the receipt and as the "what's left to do".
   *
   * An untouched box gets no list at all, not a list of five things to do. This card
   * sits directly above the picker, and every row of it is a row between the shopper
   * and the control they came here to use; the picker states each group's own
   * "Pick 1+" anyway, right where the choosing happens. The moment there is
   * something in the box, a receipt earns its place — and that is when the reminder
   * of what is still missing becomes useful rather than premature.
   */
  function summaryRows(offer, data, chosen) {
    if (offer.kind !== 'mix_and_match') {
      return offer.members.map((member) => ({ type: 'item', member, quantity: member.quantity }));
    }

    if (!chosen.some(({ picks }) => picks.length > 0)) return [];

    const rows = [];
    chosen.forEach(({ group, picks, count }) => {
      picks.forEach((pick) => rows.push({ type: 'item', member: pick, quantity: pick.quantity }));
      if (count < group.min) rows.push({ type: 'todo', group, remaining: group.min - count });
    });
    return rows;
  }

  function renderSummary(root, offer, data, totals, chosen) {
    const count = root.querySelector('[data-bundle-count]');
    if (count) {
      const label = countLabel(offer);
      count.textContent = label;
      count.hidden = !label;
    }

    const vendors = new Set();
    offer.members.forEach((member) => {
      const vendor = data.get(member.id)?.vendor;
      if (vendor) vendors.add(vendor);
    });
    const brand = root.querySelector('[data-bundle-brand]');
    if (brand) {
      // A single-brand bundle names the brand; a mixed one would be a list, which
      // belongs in the item cards rather than the header.
      brand.textContent = vendors.size === 1 ? [...vendors][0] : '';
      brand.hidden = vendors.size !== 1;
    }

    const priceNode = root.querySelector('[data-bundle-price]');
    const compareNode = root.querySelector('[data-bundle-compare]');
    const saveNode = root.querySelector('[data-bundle-save]');

    if (priceNode && totals) {
      // An unfilled box is priced from its cheapest complete configuration, and has
      // to say so — the same number unlabelled would read as the price of whatever
      // the shopper goes on to pick.
      priceNode.textContent = totals.from ? `From ${totals.total}` : totals.total;
      priceNode.removeAttribute('data-placeholder');
    }
    if (compareNode) {
      // No strike-through against a "from" price: it would be the cheapest box's
      // retail, which is not what the shopper is looking at.
      const compare = totals?.from ? '' : totals?.compare || '';
      compareNode.textContent = compare;
      compareNode.hidden = !compare || compare === totals?.total;
    }
    if (saveNode) {
      let label = '';
      if (offer.priceType === 'percentage_off' && offer.priceValue > 0) {
        label = `Save ${offer.priceValue % 1 === 0 ? offer.priceValue : offer.priceValue.toFixed(1)}%`;
      } else if (totals?.save) {
        label = `Save ${totals.save}`;
      }
      saveNode.textContent = label;
      saveNode.hidden = !label;
    }

    const rows = summaryRows(offer, data, chosen);

    // The heading names what the list is, and on a build-your-own box that is the
    // shopper's own selection rather than a manifest.
    const heading = root.querySelector('[data-bundle-heading]');
    if (heading) {
      const text = (offer.kind === 'mix_and_match' && root.dataset.headingMix) || root.dataset.heading || '';
      heading.textContent = text;
      heading.hidden = !text || rows.length === 0;
    }

    const list = root.querySelector('[data-bundle-list]');
    if (!list) return;
    list.textContent = '';

    rows.forEach((row) => {
      if (row.type === 'todo') {
        const item = el('li', 'bundle-summary__item bundle-summary__item--todo');
        const button = el('button', 'bundle-summary__todo');
        button.type = 'button';
        button.appendChild(el('span', 'bundle-summary__thumb bundle-summary__thumb--empty'));
        const label = el('span', 'bundle-summary__body');
        label.appendChild(el('span', 'bundle-summary__title', row.group.label));
        button.appendChild(label);
        button.appendChild(el('span', 'bundle-summary__item-price', `Pick ${row.remaining}`));
        // Straight to the group that still needs filling. The picker is the app's
        // widget further down the same column, and on mobile it is well below the
        // fold — a row that says what is missing but not where is only half an answer.
        button.addEventListener('click', () => {
          row.group.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        item.appendChild(button);
        list.appendChild(item);
        return;
      }

      const member = row.member;
      const node = data.get(member.id);
      const item = el('li', 'bundle-summary__item');

      const media = el('span', 'bundle-summary__thumb');
      const image = new Image();
      image.src = sized(memberImage(member, data), 160);
      image.alt = '';
      image.loading = 'lazy';
      media.appendChild(image);
      item.appendChild(media);

      const body = el('span', 'bundle-summary__body');
      const titleText = memberTitle(member, data);
      if (node?.handle) {
        const link = el('a', 'bundle-summary__title', titleText);
        link.href = memberUrl(member, data);
        body.appendChild(link);
      } else {
        body.appendChild(el('span', 'bundle-summary__title', titleText));
      }
      if (row.quantity > 1) {
        body.appendChild(el('span', 'bundle-summary__qty', `× ${row.quantity}`));
      }
      item.appendChild(body);

      // The unit price, with the `× N` beside it in the body — not the line total.
      const unitCents = memberUnitCents(data, member.id);
      if (unitCents != null) {
        item.appendChild(el('span', 'bundle-summary__item-price', formatMoney(unitCents, offer.moneyFormat)));
      }

      list.appendChild(item);
    });

    root.dataset.bundleSummaryReady = 'true';
  }

  /* ----------------------------------------------------------- trust scores */

  /** Section-rendering endpoint that answers with one product's TrustScore. */
  const TRUST_SCORE_SECTION = 'section-rendering-trust-score';

  /** How many of those to keep in flight. A build-your-own pool runs to ~18 members. */
  const TRUST_SCORE_CONCURRENCY = 6;

  const SVG_NS = 'http://www.w3.org/2000/svg';

  /**
   * The members' TrustScores, keyed by product id.
   *
   * Not part of the Storefront call above, and not for want of asking: the score is a
   * metafield (`custom.trust_score`), and the unauthenticated storefront this file
   * talks to is refused metafields outright — "Access denied for metafield field.
   * Required access: `unauthenticated_read_metafields`". Liquid can read it, but
   * Liquid cannot see these products; the offer is app-block markup that renders
   * after it. So the score is fetched the way the AI search page fetches its cards
   * (assets/birch-ai-search-page-juno.js): one Section Rendering request per product,
   * against a section that renders nothing but the number.
   *
   * Never awaited by `init` — a badge is an ornament on a row that has already told
   * the shopper what is in the box, and holding the page's ready flag for a dozen
   * round trips to add one would be the wrong trade.
   */
  async function fetchTrustScores(offer, data) {
    const wanted = offer.members
      .map((member) => ({ id: member.id, handle: data.get(member.id)?.handle }))
      .filter((entry) => entry.handle);

    const scores = new Map();
    if (wanted.length === 0) return scores;

    let cursor = 0;
    const worker = async () => {
      while (cursor < wanted.length) {
        const entry = wanted[cursor];
        cursor += 1;
        try {
          const response = await fetch(
            `/products/${encodeURIComponent(entry.handle)}?section_id=${TRUST_SCORE_SECTION}`,
            { credentials: 'same-origin' }
          );
          // A password-protected store does not fail these — fetch follows the 302
          // and hands back /password's own 200 body, which has no score in it.
          if (!response.ok || (response.redirected && /\/password(\?|$)/.test(response.url))) continue;
          const match = (await response.text()).match(/data-trust-score="(\d+)"/);
          if (match) scores.set(entry.id, Number(match[1]));
        } catch {
          // One card without a badge, not a broken row.
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(TRUST_SCORE_CONCURRENCY, wanted.length) }, worker));
    return scores;
  }

  function ringCircle(className) {
    const circle = document.createElementNS(SVG_NS, 'circle');
    circle.setAttribute('class', className);
    circle.setAttribute('cx', '20');
    circle.setAttribute('cy', '20');
    circle.setAttribute('r', '16');
    circle.setAttribute('pathLength', '100');
    return circle;
  }

  /**
   * The badge itself — the same ring, colours and geometry as the PLP card and the
   * PDP media badge, built here rather than fetched as markup because the CSS that
   * makes it a ring lives in this row's own block (blocks/bundle-contents.liquid) and
   * a section-rendered fragment would arrive without it.
   *
   * Inert, unlike the PDP's: that one is a second trigger for the breakdown dialog,
   * and there is no such dialog on a bundle page. The member's own PDP has both, and
   * the card already links there twice.
   */
  function trustBadge(score) {
    const badge = el('span', 'bundle-card__trust');
    badge.style.setProperty('--trust-score-value', String(score));
    badge.setAttribute('role', 'img');
    badge.setAttribute('aria-label', `TrustScore ${score} out of 100`);

    const disc = el('span', 'bundle-card__trust-disc');
    const ring = document.createElementNS(SVG_NS, 'svg');
    ring.setAttribute('class', 'bundle-card__trust-ring');
    ring.setAttribute('viewBox', '0 0 40 40');
    ring.setAttribute('aria-hidden', 'true');
    ring.setAttribute('focusable', 'false');
    ring.appendChild(ringCircle('bundle-card__trust-track'));
    ring.appendChild(ringCircle('bundle-card__trust-progress'));
    disc.appendChild(ring);
    disc.appendChild(el('span', 'bundle-card__trust-number', String(score)));

    badge.appendChild(disc);
    badge.appendChild(el('span', 'bundle-card__trust-label', 'TrustScore'));
    return badge;
  }

  /** Drop each score onto the corner of its card's photo, once they land. */
  function paintTrustScores(root, scores) {
    scores.forEach((score, id) => {
      root.querySelectorAll(`[data-bundle-card-id="${id}"] .bundle-card__media`).forEach((media) => {
        if (media.querySelector('.bundle-card__trust')) return;
        media.appendChild(trustBadge(score));
      });
    });
  }

  function renderContents(root, offer, data) {
    const mix = offer.kind === 'mix_and_match';

    const copy = (node, plain, forMix) => {
      if (!node) return;
      const text = (mix && root.dataset[forMix]) || root.dataset[plain] || '';
      node.textContent = text;
      node.hidden = !text;
    };
    copy(root.querySelector('[data-bundle-contents-heading]'), 'heading', 'headingMix');
    copy(root.querySelector('[data-bundle-contents-subheading]'), 'subheading', 'subheadingMix');

    const grid = root.querySelector('[data-bundle-contents-grid]');
    if (!grid) return;
    grid.textContent = '';

    const renderCard = (member, quantity) => {
      const node = data.get(member.id);
      const card = el('article', 'bundle-card');
      // How `paintTrustScores` finds this card again. Not unique on a build-your-own
      // box — the same product can be offered in two groups — which is why that
      // function badges every match rather than the first.
      card.dataset.bundleCardId = member.id;

      const media = el('div', 'bundle-card__media');
      const image = new Image();
      image.dataset.bundleCardImage = '';
      image.src = sized(memberImage(member, data), 600);
      image.alt = node?.featuredImage?.altText || memberTitle(member, data);
      image.loading = 'lazy';
      media.appendChild(image);
      if (quantity > 1) media.appendChild(el('span', 'bundle-card__qty', `× ${quantity}`));
      card.appendChild(media);

      const body = el('div', 'bundle-card__body');
      if (node?.vendor) body.appendChild(el('p', 'bundle-card__vendor', node.vendor));

      const heading = el('h3', 'bundle-card__title');
      const titleText = memberTitle(member, data);
      if (node?.handle) {
        const link = el('a', null, titleText);
        link.dataset.bundleCardTitle = '';
        link.href = memberUrl(member, data);
        heading.appendChild(link);
      } else {
        heading.textContent = titleText;
      }
      body.appendChild(heading);

      const unitCents = memberUnitCents(data, member.id);
      if (unitCents != null) {
        body.appendChild(el('p', 'bundle-card__price', formatMoney(unitCents, offer.moneyFormat)));
      }

      const text = excerpt(node?.description?.value || node?.descriptionHtml, 260);
      if (text) body.appendChild(el('p', 'bundle-card__text', text));

      if (node?.handle) {
        const more = el('a', 'bundle-card__link', 'View product details');
        more.href = `/products/${node.handle}`;
        body.appendChild(more);
      }

      card.appendChild(body);
      return card;
    };

    if (!mix) {
      offer.members.forEach((member) => grid.appendChild(renderCard(member, member.quantity)));
      root.dataset.bundleContentsReady = 'true';
      return;
    }

    // A build-your-own box is a set of decisions, so this row is laid out as those
    // decisions: one shelf per group, headed by what the group is and how many of it
    // the shopper gets to pick. Flattening the pool into one 18-card grid would lose
    // the only thing that makes it navigable — that these three are the diaper balms
    // and those five are the bath toys.
    grid.classList.add('bundle-contents__grid--grouped');

    offer.groups.forEach((group) => {
      const section = el('section', 'bundle-contents__group');

      const header = el('div', 'bundle-contents__group-header');
      header.appendChild(el('h3', 'bundle-contents__group-title', group.label));
      header.appendChild(el('span', 'bundle-contents__group-pick', pickLabel(group)));
      section.appendChild(header);

      const shelf = el('div', 'bundle-contents__shelf');
      group.options.forEach((option) => shelf.appendChild(renderCard(option, 1)));
      section.appendChild(shelf);

      grid.appendChild(section);
    });

    root.dataset.bundleContentsReady = 'true';
  }

  /**
   * Bring a fixed bundle's "what's inside" cards up to the variant each row is now
   * set to: photo, name and link. In place rather than re-rendered, so the TrustScore
   * badges `paintTrustScores` has already added stay where they are.
   */
  function refreshContents(root, offer, data) {
    offer.members.forEach((member) => {
      const card = root.querySelector(`[data-bundle-card-id="${member.id}"]`);
      if (!card) return;

      const image = card.querySelector('[data-bundle-card-image]');
      const src = sized(memberImage(member, data), 600);
      if (image && src && image.getAttribute('src') !== src) image.src = src;

      const link = card.querySelector('[data-bundle-card-title]');
      if (!link) return;
      const title = memberTitle(member, data);
      if (link.textContent !== title) link.textContent = title;
      const href = memberUrl(member, data);
      if (href && link.getAttribute('href') !== href) link.setAttribute('href', href);
    });
  }

  /* ------------------------------------------------------------ bundle count */

  /*
   * "How many of this bundle" — the one thing on this page the app has no concept
   * of. Its own add-to-cart posts exactly one bundle, and its widget offers no way
   * to ask for two.
   *
   * A bundle is not a line: it is a *set* of lines sharing one `_ssBundleKey`, and
   * the app's discount Function prices each such set independently (verified live —
   * two keys on one cart produced two separate "Postpartum Pain Relief Bundle"
   * allocations). So N bundles are N independently keyed sets, and the way to ask
   * for them is to post the app's own `items` array N times over with a fresh key
   * on each copy.
   *
   * The tempting shortcut — leave one set and multiply every line's quantity — is
   * wrong. It happens to look right for this offer, because 20% off a doubled set
   * is double the discount, but a `fixed_price` or `amount_off` bar (both supported
   * by the app) would take its single discount off a double-sized set and sell two
   * bundles for the price of one. Also verified live: the quantity-multiplied cart
   * came back a cent adrift of two real bundles even on the percentage bar.
   *
   * Rather than rebuild the payload — variant resolution, gifts, selling plans and
   * the datepicker all live in the app — we let the app build it and rewrite the
   * request on its way out.
   */

  /** Only requests fired by a real click on this page's buy buttons are rewritten. */
  let pendingBundleCopies = 0;

  /** When that arming expires. See `armBundleCopies` for why it is not one number. */
  let pendingBundleDeadline = 0;

  /**
   * Add to cart POSTs inside the click's own task, so it needs almost no window.
   * Buy it now clears the cart first and only adds once that round trip comes back,
   * which is why the window cannot simply be "this tick" — seeing the clear go out
   * is the signal to hold it open for the add behind it.
   */
  const ADD_ARM_MS = 1500;
  const CHECKOUT_ARM_MS = 8000;

  /** Monotonic per-page add counter, so two adds never share a generated key. */
  let bundleAddSequence = 0;

  function requestedBundleCount() {
    const input = document.querySelector('input[name="bundle_quantity"]');
    if (!input) return 1;
    const requested = Number.parseInt(input.value, 10);
    if (!Number.isFinite(requested) || requested < 1) return 1;
    const max = Number.parseInt(input.max, 10);
    return Number.isFinite(max) && max > 0 ? Math.min(requested, max) : requested;
  }

  /**
   * One bundle's worth of lines repeated `copies` times, each repeat carrying its
   * own `_ssBundleKey`.
   *
   * Only lines that already have a key are repeated. Two kinds of line the app
   * appends to the same payload deliberately have none, and both are passed through
   * once rather than multiplied:
   *
   * - add-ons (`_ssBundleAddOn`) — one-time upsells attached to the add, which the
   *   app treats as riding alongside the bundle rather than being part of it;
   * - free gifts (`_bundle_gift`) — granted by the bar, and zeroed by the app's
   *   discount Function under its own cap. Repeating them would post free product
   *   the Function has not agreed to, so a three-bundle add asks for one gift. If a
   *   bar is ever meant to grant a gift per box, that is a decision for the app's
   *   Function, not for a theme rewriting its payload.
   *
   * The key is rewritten even for a single bundle, and that is deliberate. The app
   * numbers its keys from a counter that restarts at 1 on every page load, so
   * "add, reload, add again" posts two sets under the same key and Shopify silently
   * merges them into one double-quantity set — precisely the mispriced shape
   * described above. A per-add token cannot collide across loads.
   *
   * The one thing added rather than rewritten is `_bundleProductTitle`. The app names
   * its cart lines after the tier (`_bundle_block_name`), and a tier is titled for the
   * widget, not for the cart: a mix tier heads its picker ("Build your bundle"), and a
   * fixed one keeps the app's default unless someone renames it — every Fall 2026 set
   * went live as "Product group". What the shopper bought is the anchor product, and
   * that is what the row should say. Written under our own name rather than over the
   * app's: `_bundle_block_name` follows the line through checkout into subscription
   * contract attributes and order analytics, where it identifies the bar it came from,
   * and that is the app's to define.
   */
  function expandBundleItems(items, copies) {
    const token = `${Date.now().toString(36)}${(bundleAddSequence += 1)}`;
    const expanded = [];

    // Every line of one row needs the same name — the cart reads it off whichever
    // lands first.
    const named = ANCHOR_TITLE ? { _bundleProductTitle: ANCHOR_TITLE } : null;

    for (let copy = 0; copy < copies; copy += 1) {
      for (const item of items) {
        const properties = item?.properties;
        if (!properties || properties._ssBundleKey == null) {
          if (copy === 0) expanded.push(item);
          continue;
        }
        expanded.push({
          ...item,
          properties: {
            ...properties,
            ...named,
            _ssBundleKey: `${properties._ssBundleKey}-${token}-${copy}`,
          },
        });
      }
    }

    return expanded;
  }

  /** The rewritten `/cart/add.js` body, or null to leave the request alone. */
  function expandBundleAddBody(body, copies) {
    let payload;
    try {
      payload = JSON.parse(body);
    } catch {
      return null;
    }
    if (!Array.isArray(payload?.items)) return null;
    if (!payload.items.some((item) => item?.properties?._ssBundleKey != null)) return null;

    return JSON.stringify({ ...payload, items: expandBundleItems(payload.items, copies) });
  }

  /** True while a click on this page's buy buttons is still plausibly in flight. */
  function bundleCopiesArmed() {
    return pendingBundleCopies > 0 && Date.now() < pendingBundleDeadline;
  }

  function installBundleQuantityShim() {
    // Anything else on the page that adds to the cart — cart-restore.js replaying a
    // buy-now snapshot, cart-sync.js reconciling another device — posts the very
    // same shape and must pass through untouched. So the shim is armed by a click
    // on this page's own buy buttons and nothing else. A document-level capture
    // listener is guaranteed to run first: the app binds to the form and to the
    // submit button, both descendants (see its interceptBuyNowForMultiVariant).
    //
    // The arming has to expire, because plenty of clicks never produce an add at
    // all: on a build-your-own box, every tap on Add to cart before the box is
    // full is answered with "pick 1 more" and no request. Leaving the shim live
    // for seconds afterwards would put it in the path of whatever the theme's own
    // cart machinery did next — cart-sync reconciles on `visibilitychange`, which
    // is one tab switch away — and those lines carry the same bundle properties,
    // so they would be multiplied by a stepper the shopper never associated
    // with them.
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target?.closest('add-to-cart-component, button[name="add"], .accelerated-checkout-block--bundle')) {
          return;
        }
        pendingBundleCopies = requestedBundleCount();
        pendingBundleDeadline = Date.now() + ADD_ARM_MS;
      },
      true
    );

    const nativeFetch = window.fetch.bind(window);

    window.fetch = function bundleAwareFetch(resource, options) {
      try {
        if (bundleCopiesArmed()) {
          const url = typeof resource === 'string' ? resource : String(resource?.url ?? '');
          const method = String(options?.method ?? 'GET').toUpperCase();

          // The buy-now path empties the cart first and adds only once that comes
          // back; hold the arming open for the add we now know is coming.
          if (method === 'POST' && url.includes('/cart/clear')) {
            pendingBundleDeadline = Date.now() + CHECKOUT_ARM_MS;
          } else if (method === 'POST' && url.includes('/cart/add') && typeof options?.body === 'string') {
            const body = expandBundleAddBody(options.body, pendingBundleCopies);
            pendingBundleCopies = 0;
            if (body) return nativeFetch(resource, { ...options, body });
          }
        }
      } catch (error) {
        // Never let this stop a shopper checking out — worst case they get one bundle.
        console.error('Bundle quantity shim failed:', error);
      }

      return nativeFetch(resource, options);
    };
  }

  /* --------------------------------------------------------------------- run */

  /**
   * The money to show for the box as it stands.
   *
   * Ours is the fallback in both directions: before the app has priced the bar, and
   * for the "from" price of a box nobody has started filling (which the app leaves
   * blank, having nothing to add up). Once it has picks and a total, its strings
   * win — they come from the shop's own Liquid `money` filters and are the numbers
   * its discount Function will apply at checkout.
   *
   * When the app's total disagrees with what we computed — a pick on a variant other
   * than the first, most often — the rest of our arithmetic is dropped rather than
   * reconciled onto their number: re-parsing two formatted amounts in an unknown
   * locale to recover the difference is not a trade worth making.
   */
  function currentTotals(offer, data, chosen) {
    const hasPicks = chosen.some(({ picks }) => picks.length > 0);
    if (!hasPicks) {
      const floor = computeMixFloor(offer, data);
      return floor ? { ...floor, from: true } : null;
    }

    const computed = computeMixTotals(offer, data, chosen);
    const fromApp = appTotals(offer);
    if (!fromApp) return computed;

    const agrees = computed && computed.total === fromApp.total;
    return {
      ...(agrees ? computed : {}),
      total: fromApp.total,
      compare: fromApp.compare || (agrees ? computed.compare : ''),
    };
  }

  /**
   * The page for a bundle that cannot currently be assembled.
   *
   * Everything that offers it goes: the picker, the price, the running receipt, the
   * pool of options, and the buy buttons (that last pair in CSS — see
   * blocks/_bundle-summary.liquid). What is left is the product itself — title,
   * description, photographs — under one line saying there is nothing to click,
   * which is how every other sold-out page on the shop reads.
   *
   * Deliberately not left to the app. Its widget would walk a shopper through
   * filling a box whose missing shelf it refuses at add-to-cart: the whole choosing
   * done, and the news broken at the last possible moment. A page that says so up
   * front is the better version of the same answer.
   *
   * The gallery stays and is rendered from the pool exactly as a browsable box would
   * be — the photographs are what the page still has to show.
   */
  function renderUnavailable({ gallery, summary, contents }, offer, data) {
    if (gallery) renderGallery(gallery, offer, data, []);
    contents?.setAttribute('hidden', '');

    const notice = summary?.querySelector('[data-bundle-unavailable]');
    if (notice) notice.hidden = false;

    // No `data-bundle-pdp-ready`: readiness is the signal that this script has taken
    // the page over and the app's controls can go, and here the controls are going
    // for the opposite reason. The kind rides along as it always does.
    document.documentElement.setAttribute('data-bundle-pdp-kind', offer.kind);
    document.documentElement.setAttribute('data-bundle-pdp-unavailable', 'true');
  }

  /**
   * The picker's own group counters, restated without a ceiling nobody can reach.
   *
   * The app builds that line — "0 / 100 · Pick 1 to 100" — from a template on the
   * mix container, and that template is a single string shared by every group, so
   * it cannot be told that this group is open-ended and that one is not. The text
   * is rewritten per group here instead, after each of the app's own renders.
   *
   * Only open-ended groups are touched; a genuine range keeps the app's wording.
   * What is left is the two things a shopper needs from this line — what the group
   * still asks for, and, once it is met, what they have put in it. The `is-met`
   * class the app toggles goes on colouring it, so meeting the floor still reads as
   * the tick it did before.
   *
   * Written only where it differs: `watchPicks` observes this subtree, and
   * `textContent` replaces the node whether or not the string changed, so an
   * unconditional write would schedule the next paint from inside its own.
   */
  function relabelPickCounters(chosen) {
    chosen.forEach(({ group, count }) => {
      if (pickMax(group) !== null) return;
      const counter = group.el.querySelector('[data-mix-counter]');
      if (!counter) return;

      const text = count === 0 ? pickLabel(group) : count < group.min ? `${count} of ${group.min}` : `${count} selected`;
      if (counter.textContent !== text) counter.textContent = text;
      // What snippets/bundle-picker-styles.liquid waits for before showing the line
      // at all. Set after the first write, and not part of the observed attributes.
      counter.dataset.pickRestated = 'true';
    });
  }

  /**
   * Keep the page in step with the picker.
   *
   * Everything the shopper does lives inside the app's tier — cards gain
   * `.is-selected`, steppers change, the header total is rewritten — so one observer
   * over that subtree catches all of it. Renders are coalesced into an animation
   * frame because a single tap fires several mutations (class, stepper, price), and
   * the gallery only rebuilds when the picks themselves changed: the app also
   * rewrites its total on things that leave the box alone, and a hero that flickers
   * on every one of those would be worse than one that lags a frame.
   */
  function watchPicks(offer, data, { gallery, summary }) {
    let lastSignature = null;

    const paint = () => {
      const chosen = readPicks(offer);
      const signature = picksSignature(chosen);
      const changed = signature !== lastSignature;
      lastSignature = signature;

      // Re-asserted on every paint rather than once: the app redraws a card when it is
      // picked, and `labelWidget` is a no-op on the cards it has already named.
      labelWidget(offer);
      relabelPickCounters(chosen);

      if (gallery && changed) renderGallery(gallery, offer, data, chosen);
      if (summary) renderSummary(summary, offer, data, currentTotals(offer, data, chosen), chosen);

      // Same rule as the gallery: rebuild on a real change, and otherwise just keep
      // the total current. Rebuilding a 144-option pool on every mutation frame of a
      // picker the shopper is dragging through would be work nobody asked for.
      if (changed) publishBundleContext(aiMixBundle(offer, data, chosen));
      else refreshBundleTotal(offer);
    };

    let queued = false;
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        paint();
      });
    };

    paint();
    new MutationObserver(schedule).observe(offer.tier, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    // A new size in a card's select is not a mutation of anything the observer sees,
    // and it is a change of the box: its photo, its receipt line.
    offer.tier.addEventListener('change', schedule);
  }

  /**
   * Keep a fixed bundle's page in step with its rows' variant pickers.
   *
   * Nothing is picked on a fixed bundle, but a member with sizes or colours is still a
   * choice, made in the app's selects on its row. The app records it as the row's
   * `data-selected-variant-id` — the variant it will add — so that attribute is what
   * is watched, and a change of it re-renders the gallery and brings the "what's
   * inside" cards up to date. The row labels are re-asserted on every frame, for the
   * same reason as on a build-your-own box.
   */
  function watchChoices(offer, data, { gallery, contents }) {
    const signatureOf = () => offer.members.map((member) => member.row.dataset.selectedVariantId || '').join('|');
    let lastSignature = signatureOf();

    const paint = () => {
      labelWidget(offer);

      const signature = signatureOf();
      if (signature === lastSignature) return;
      lastSignature = signature;

      if (gallery) renderGallery(gallery, offer, data);
      if (contents) refreshContents(contents, offer, data);
      publishBundleContext(aiFixedBundle(offer, data));
    };

    let queued = false;
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        paint();
      });
    };

    labelWidget(offer);
    new MutationObserver(schedule).observe(offer.tier, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-selected-variant-id'],
    });
    offer.tier.addEventListener('change', schedule);
  }

  /* ------------------------------------------------- telling the AI companion */

  /**
   * What the Birch AI assistant is told about this box.
   *
   * The chat panel reads `window.BirchAI.context` at the moment a message is sent
   * (assets/birch-ai-companion.js), so writing the box into it here — and
   * rewriting it as the shopper picks — is all the worker needs to see the box as
   * it stands. Nothing is fetched for this: every field is already in `offer` and
   * `data`, because the page you are looking at was rendered from them.
   *
   * IDENTITY AND SHAPE ONLY, deliberately. Handles, quantities and group limits go
   * up; prices, stock and certifications do not. The worker resolves those from the
   * catalog by handle, so a fact the assistant states about a member is one the
   * catalog holds rather than one this page computed — the same rule the PDP
   * grounding block follows. The two numbers that do go up are the bar's own deal
   * and the total the app has painted, and both are labelled as what they are: the
   * offer's terms, and a string to defer to, not a claim about a product.
   *
   * Sizing matters here in a way it doesn't for the rest of this file: a mix pool on
   * this store already runs to 144 options across five groups, and whatever is sent
   * goes into a prompt on every turn of the conversation. So the shopper's PICKS are
   * always sent whole — they are what "is this box any good" is about — while the
   * pool is capped per group and overall, with `optionCount` left honest so the
   * assistant can say "24 of the 61 shampoos" rather than imply it has seen them all.
   */
  const AI_POOL_PER_GROUP = 24;
  const AI_POOL_TOTAL = 60;

  /**
   * One member, as the worker sees it.
   *
   * The id rides along with the handle because the handle is the one field here
   * that depends on the Storefront call having succeeded — and the worker can turn
   * an id back into a handle from its own catalog map. A member that lost both is
   * still worth sending under its title: "we can't look that one up" is a better
   * answer than a box described one product short.
   */
  function aiMember(member, data, quantity) {
    const entry = { id: member.id, title: memberTitle(member, data) };
    const handle = data.get(member.id)?.handle;
    if (handle) entry.handle = handle;
    if (quantity != null) entry.quantity = quantity;
    return entry;
  }

  /** Groups whose minimum the shopper has not met yet, and by how much. */
  function shortfallFrom(chosen) {
    return chosen
      .filter(({ group, count }) => count < group.min)
      .map(({ group, count }) => ({ group: group.label, need: group.min - count }));
  }

  function aiDeal(offer) {
    return { type: offer.priceType, value: offer.priceValue };
  }

  function aiFixedBundle(offer, data) {
    const members = offer.members.slice(0, AI_POOL_TOTAL);
    return {
      kind: 'product_group',
      name: offer.name,
      deal: aiDeal(offer),
      members: members.map((member) => aiMember(member, data, member.quantity)),
      ...(members.length < offer.members.length && { membersTruncated: true }),
      // Stated rather than implied: the sold-out payload says `available: false`, and
      // a field that only ever appears in one of the two reads as a missing field.
      available: true,
      hydrated: data.size > 0,
      displayedTotal: appTotals(offer)?.total || '',
    };
  }

  function aiMixBundle(offer, data, chosen) {
    // The overall budget is spent group by group, so a first group with 60 options
    // cannot leave the last one unrepresented — every group gets at least its share
    // of the cap before any group gets more.
    let budget = AI_POOL_TOTAL;
    const groups = offer.groups.map((group) => {
      const options = group.options.slice(0, Math.max(0, Math.min(AI_POOL_PER_GROUP, budget)))
        .map((option) => aiMember(option, data));
      budget -= options.length;
      // `max` is omitted rather than sent as the app's stand-in ceiling: a group with
      // no real limit is one the assistant should describe as open, and a field that
      // says 100 is one it will quote. See `pickMax`.
      const max = pickMax(group);
      return {
        label: group.label,
        min: group.min,
        ...(max !== null && { max }),
        optionCount: group.options.length,
        options,
        ...(options.length < group.options.length && { optionsTruncated: true }),
      };
    });

    const shortfall = shortfallFrom(chosen);

    return {
      kind: 'mix_and_match',
      name: offer.name,
      deal: aiDeal(offer),
      includesCurrent: offer.includesCurrent,
      minPicks: offer.minPicks,
      ...(offer.maxPicks !== null && { maxPicks: offer.maxPicks }),
      groups,
      picks: chosen.flatMap(({ group, picks }) =>
        picks.map((pick) => ({ ...aiMember(pick, data, pick.quantity), group: group.label }))
      ),
      shortfall,
      complete: shortfall.length === 0,
      available: true,
      hydrated: data.size > 0,
      displayedTotal: appTotals(offer)?.total || '',
    };
  }

  /**
   * `kind: 'none'` is not an empty state, it is an answer: this product sits on the
   * bundle template but carries no offer. The theme knows the difference and the
   * worker cannot infer it — `templateSuffix` alone says "bundle" for this page and
   * for a page whose script failed, which are opposite situations for an assistant
   * (one is an ordinary product, the other is a box it must admit it cannot read).
   */
  function publishBundleContext(bundle) {
    const birch = (window.BirchAI = window.BirchAI || {});
    birch.context = birch.context || {};
    birch.context.bundle = bundle;
  }

  /**
   * The app rewrites its total on things that leave the box alone, so the published
   * structure is rebuilt only when the picks change (see `watchPicks`) — but the
   * total it is checked against has to keep up regardless, or the assistant is left
   * comparing its own arithmetic to a stale string and declining to quote a price
   * that is right there on the page.
   */
  function refreshBundleTotal(offer) {
    const bundle = window.BirchAI?.context?.bundle;
    if (!bundle || bundle.kind === 'none') return;
    bundle.displayedTotal = appTotals(offer)?.total || '';
  }

  /* ------------------------------------------------------ adding from the chat */

  /** Backoff for confirming an add, in ms. Six reads over ~7s, front-loaded. */
  const ADD_POLL_STEPS = [300, 500, 800, 1200, 1800, 2500];

  /** Total units in the cart, or null when the cart could not be read. */
  async function cartUnitCount() {
    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const cart = await response.json();
      return Array.isArray(cart.items)
        ? cart.items.reduce((sum, item) => sum + (item.quantity || 0), 0)
        : null;
    } catch {
      return null;
    }
  }

  /**
   * The page's own Add to cart button — the main one, not the sticky bar's copy of
   * it. Either works (the app binds to both), but the main one is the control the
   * shopper would have pressed, and a click on a bar that is scrolled out of view
   * is harder to reason about when something goes wrong.
   */
  function addToCartButton() {
    const buttons = [...document.querySelectorAll('add-to-cart-component button[name="add"]:not([disabled])')];
    return buttons.find((button) => !button.closest('sticky-add-to-cart')) || buttons[0] || null;
  }

  /**
   * `window.BirchAI.bundle` — how the assistant adds THIS box to the cart.
   *
   * It presses the page's own button rather than posting a payload. The correctly
   * tagged multi-line body is the app's to build and the shim above's to expand, and
   * a second implementation of that contract living in the chat path is exactly the
   * drift this file opens by refusing to introduce. Pressing the button also arms
   * the shim the way a shopper's press does, so "add three of them" works for free.
   *
   * The outcome is verified, not assumed. The app answers an under-filled box by
   * refusing to post at all, so an assistant that reported success on the strength
   * of having clicked would be wrong in precisely the case the shopper most needs
   * to hear otherwise. When the cart cannot be read at all we say so (`verified:
   * false`) rather than promote a hopeful click to a fact.
   */
  async function addBundleToCart(copies) {
    const offer = currentOffer;
    if (!offer) return { ok: false, reason: 'no_bundle' };
    // Sold out below the surface: the anchor product is a $0 placeholder and still
    // reports itself as available, so this is the only place that knows.
    if (offer.unavailable) return { ok: false, reason: 'unavailable', soldOut: offer.unavailable };

    if (offer.kind === 'mix_and_match') {
      const shortfall = shortfallFrom(readPicks(offer));
      if (shortfall.length > 0) return { ok: false, reason: 'incomplete', shortfall };
    }

    const button = addToCartButton();
    if (!button) return { ok: false, reason: 'no_button' };

    // How many copies we can actually honour: the stepper is what the shim reads at
    // click time, so without one on the page a request for three boxes is a request
    // we can only half-keep — and the number we report back is the one that lands.
    const input = document.querySelector('input[name="bundle_quantity"]');
    const max = Number.parseInt(input?.max ?? '', 10);
    const wanted = Math.max(1, Number.parseInt(copies, 10) || 1);
    const applied = input ? (Number.isFinite(max) && max > 0 ? Math.min(wanted, max) : wanted) : 1;
    if (input && input.value !== String(applied)) {
      input.value = String(applied);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const before = await cartUnitCount();
    button.click();

    if (before == null) return { ok: true, copies: applied, verified: false };

    for (const step of ADD_POLL_STEPS) {
      await new Promise((resolve) => setTimeout(resolve, step));
      const after = await cartUnitCount();
      if (after != null && after > before) {
        return { ok: true, copies: applied, verified: true, lines: after - before };
      }
    }
    return { ok: false, reason: 'not_added' };
  }

  /** The offer this page is showing, once init has read it. Null until then. */
  let currentOffer = null;

  function publishBundleApi() {
    const birch = (window.BirchAI = window.BirchAI || {});
    birch.bundle = {
      /** True when the box is in a state the app will accept. */
      ready: () => {
        if (!currentOffer || currentOffer.unavailable) return false;
        if (currentOffer.kind !== 'mix_and_match') return true;
        return shortfallFrom(readPicks(currentOffer)).length === 0;
      },
      addToCart: addBundleToCart,
    };
  }

  async function init() {
    const gallery = document.querySelector('[data-bundle-gallery]');
    const summary = document.querySelector('[data-bundle-summary]');
    const contents = document.querySelector('[data-bundle-contents]');
    if (!gallery && !summary && !contents) return;

    let offer = readOffer();
    if (!offer) {
      // No bundle on this page — leave the app's own widget alone and take the
      // theme's bundle scaffolding off the page rather than showing empty panels.
      // (Also the state a non-bundle product on this template lands in.)
      publishBundleContext({ kind: 'none' });
      [gallery, summary, contents].forEach((node) => node?.setAttribute('hidden', ''));
      return;
    }

    const data = await fetchMembers(offer);
    offer = pruneUnavailable(offer, data);
    currentOffer = offer;

    // A box with a shelf that cannot be filled is not a box with a caveat, so nothing
    // below this line runs: no picker to frame, no price to quote, no contents to
    // browse. `currentOffer` is set first so the assistant's `ready()` answers no.
    if (offer.unavailable) {
      renderUnavailable({ gallery, summary, contents }, offer, data);
      publishBundleContext({
        kind: offer.kind,
        name: offer.name,
        available: false,
        soldOut: offer.unavailable,
        hydrated: data.size > 0,
      });
      return;
    }

    // After pruning, so an option is only ever named against siblings the shopper can
    // actually see: a family whose second member has been unpublished is down to one
    // card, and one card needs no telling apart.
    if (offer.kind === 'mix_and_match') {
      offer.groups.forEach((group) => {
        labelFamilyMembers(group.options, data);
        nameOnlyVariants(group.options, data);
      });
    } else {
      labelFamilyMembers(offer.members, data);
    }

    if (contents) {
      renderContents(contents, offer, data);
      // Deliberately not awaited: one request per member, and the row is complete
      // and readable without them. See `fetchTrustScores`.
      fetchTrustScores(offer, data).then((scores) => paintTrustScores(contents, scores));
    }

    if (offer.kind === 'mix_and_match') {
      // A build-your-own box has no contents until the shopper gives it some, so
      // there is no one-shot render here: the gallery and the summary are wired to
      // the picker and follow it for the life of the page. The assistant's view of
      // the box is published from the same paint, for the same reason.
      watchPicks(offer, data, { gallery, summary });
    } else {
      publishBundleContext(aiFixedBundle(offer, data));

      if (gallery) renderGallery(gallery, offer, data);
      // Straight after the render it keeps current, so a variant the app settles on
      // while it hydrates (a sold-out default swapped for one in stock) is caught.
      watchChoices(offer, data, { gallery, contents });

      let totals = computeTotals(offer, data);
      if (summary) renderSummary(summary, offer, data, totals);

      // Prefer the app's own money strings over ours whenever it produces them.
      const priced = await whenAppPriced(offer);
      if (priced && summary) {
        const fromApp = appTotals(offer);
        if (fromApp) {
          totals = { ...(totals || {}), total: fromApp.total, compare: fromApp.compare || totals?.compare || '' };
          renderSummary(summary, offer, data, totals);
        }
      }
      // Published a second time only because the app's total lands after the first:
      // `displayedTotal` is the string the assistant checks its own arithmetic
      // against, and an empty one costs it the ability to quote a price at all.
      refreshBundleTotal(offer);
    }

    // Only now hand the page over: until these land, CSS keeps this card's own item
    // list on the page, so a failed hydration degrades to a working (if plain) page
    // instead of a bundle with no price and no way to see what is in it. The kind
    // rides along because the two bundle shapes want different things from the card
    // — a fixed bundle's widget already lists every row, with its pickers, so the
    // card's list would say it twice; a build-your-own box's list is the shopper's
    // running receipt.
    document.documentElement.setAttribute('data-bundle-pdp-kind', offer.kind);
    document.documentElement.setAttribute('data-bundle-pdp-ready', 'true');
  }

  // Installed before anything can be clicked, and independently of init(): a failed
  // hydration still leaves the app's own widget usable, and the stepper has to keep
  // working with it.
  installBundleQuantityShim();

  // Registered up front for the same reason. `addToCart` refuses while `currentOffer`
  // is null, so a chat that asks to add a box before (or instead of) hydration is
  // told no rather than clicking a button nobody has finished wiring.
  publishBundleApi();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
